'use client';

import { ButtonHTMLAttributes } from 'react';
import { cn, TButtonProps } from 'types';
import { LoadingSpinner } from '../loading-spinner';
import React from 'react';

type TButton = TButtonProps & ButtonHTMLAttributes<HTMLButtonElement>;

const variantStyles = {
  purple: {
    solid: {
      disabled: 'border-2 border-transparent bg-neutralPlus text-neutralGrey cursor-not-allowed',
      enabled:
        'border-2 border-transparent bg-darkBlue text-white enabled:hover:bg-ultramarine focus:ring-turquoise focus:outline-none focus:ring',
    },
    outline: {
      disabled: 'border-2 border-neutralPlus text-neutralPlus cursor-not-allowed',
      enabled:
        'border-2 border-darkBlue text-black enabled:hover:bg-blue20 !focus:outline-none !focus:shadow-[0_0_0_3px_var(--color-blue60)] enabled:active:bg-blue60',
    },
  },
  teal: {
    solid: {
      disabled: 'border-2 border-transparent bg-neutralGrey text-neutralPlus cursor-not-allowed',
      enabled:
        'border-2 border-transparent bg-turquoise text-black enabled:hover:bg-turquoise60 focus:ring-markTurquoise focus:ring focus:border-markTurquoise focus-visible:outline-none',
    },
    outline: {
      disabled: 'border-2 border-neutralPlus text-neutralPlus cursor-not-allowed',
      enabled: 'border-2 border-turquoise text-black enabled:hover:bg-turquoise20 focus:ring-ultramarine',
    },
  },
  yellow: {
    solid: {
      disabled: 'border-2 border-transparent bg-neutralGrey text-neutralPlus',
      enabled:
        'border-2 border-transparent bg-oker text-black enabled:hover:bg-oker60 focus:ring-oker focus:border-morkOker focus:ring',
    },
    outline: {
      disabled: 'border-2 border-neutralPlus text-neutralPlus',
      enabled: 'border-2 enabled:hover:bg-oker20 border-morkOker text-black',
    },
  },
  red: {
    solid: {
      disabled: 'border-2 border-transparent bg-neutralPlus text-neutralGrey',
      enabled:
        'border-2 border-transparent bg-poppy text-white enabled:hover:bg-poppy60 focus:ring-poppy focus:border-poppy500 focus:ring',
    },
    outline: {
      disabled: 'border-2 border-neutralPlus text-neutralPlus',
      enabled: 'border-2 border-poppy text-black enabled:hover:bg-poppy20 focus:ring-poppy',
    },
  },
  black: {
    solid: {
      disabled: 'bg-neutralPlus text-neutralGrey',
      enabled: 'bg-black text-white enabled:hover:bg-black60 focus:ring-black focus:border-black500 focus:ring-[3px]',
    },
    outline: {
      disabled: 'border-2 border-neutralPlus text-neutralPlus',
      enabled: 'border-2 border-black text-black enabled:hover:bg-black20 focus:ring-black',
    },
  },
};

const variantLoaderColor = {
  purple: 'var(--color-white)',
  teal: 'var(--color-black)',
  yellow: 'var(--color-black)',
  red: 'var(--color-white)',
  black: 'var(--color-white)',
};

export const Button = ({
  children,
  variant = 'purple',
  appearance = 'solid',
  disabled = false,
  className = '',
  isLoading = false,
  loaderColor,
  onClick,
}: TButton) => {
  const [disable, setDisabled] = React.useState<boolean>(disabled);

  React.useEffect(() => {
    setDisabled(disabled);
  }, [disabled]);
  return (
    <button
      disabled={disable}
      className={cn(
        'inline-flex px-4 py-3 justify-center items-center gap-2 rounded-lg',
        'text-base font-extrabold transition-all',
        disabled ? 'cursor-initial' : 'cursor-pointer',
        variantStyles[variant][appearance][disabled ? 'disabled' : 'enabled'],
        className
      )}
      onClick={onClick}
    >
      {isLoading ? (
        <LoadingSpinner
          size={26}
          color={loaderColor || appearance == 'outline' ? 'var(--color-black)' : variantLoaderColor[variant]}
        />
      ) : (
        children
      )}
    </button>
  );
};
